import { supabase } from './client';
import { normalizeProfile } from './utils';
import { NoteItem } from './notes';
import { ProductItem } from './products';
import { UserProfile } from './profiles';

export interface SearchResult {
  type: 'note' | 'product' | 'profile';
  id: string;
  title?: string; // For notes and products
  username?: string; // For profiles
  verification_status?: 'pending' | 'submitted' | 'verified' | 'rejected' | 'expired';
  description?: string; // For products
  content?: string; // For notes
  cover_url?: string | null; // For products
  avatar_url?: string | null; // For profiles
  created_at?: string; // For notes and products
  seller_id?: string; // For products
  user_id?: string; // For notes
}

export const globalSearch = async (searchTerm: string): Promise<SearchResult[]> => {
  if (!searchTerm.trim()) {
    return [];
  }

  const results: SearchResult[] = [];
  const lowerCaseSearchTerm = `%${searchTerm.toLowerCase()}%`;

  // Search in Notes
  const { data: notesData, error: notesError } = await supabase
    .from('notes')
    .select('id, title, content, created_at, user_id, profiles(username, avatar_url, verification_status)')
    .or(`title.ilike.${lowerCaseSearchTerm},content.ilike.${lowerCaseSearchTerm}`)
    .order('created_at', { ascending: false });

  if (notesError) {
    console.error('Error searching notes:', notesError);
  } else if (notesData) {
    notesData.forEach((note: any) => {
      results.push({
        type: 'note',
        id: note.id,
        title: note.title,
        content: note.content,
        created_at: note.created_at,
        user_id: note.user_id,
        username: normalizeProfile(note.profiles)?.username,
        avatar_url: normalizeProfile(note.profiles)?.avatar_url,
        verification_status: normalizeProfile(note.profiles)?.verification_status,
      });
    });
  }

  // Search in Products
  const { data: productsData, error: productsError } = await supabase
    .from('products')
    .select('id, title, description, author, cover_url, created_at, seller_id, profiles!books_owner_id_fkey(username, avatar_url, verification_status)')
    .or(`title.ilike.${lowerCaseSearchTerm},description.ilike.${lowerCaseSearchTerm},author.ilike.${lowerCaseSearchTerm}`)
    .order('created_at', { ascending: false });

  if (productsError) {
    console.error('Error searching products:', productsError);
  } else if (productsData) {
    productsData.forEach((product: any) => {
      results.push({
        type: 'product',
        id: product.id,
        title: product.title,
        description: product.description,
        cover_url: product.cover_url,
        created_at: product.created_at,
        seller_id: product.seller_id,
        username: normalizeProfile(product.profiles)?.username,
        avatar_url: normalizeProfile(product.profiles)?.avatar_url,
        verification_status: normalizeProfile(product.profiles)?.verification_status,
      });
    });
  }

  // Search in Profiles
  const { data: profilesData, error: profilesError } = await supabase
    .from('profiles')
    .select('id, username, avatar_url, verification_status')
    .ilike('username', lowerCaseSearchTerm)
    .order('username', { ascending: true });

  if (profilesError) {
    console.error('Error searching profiles:', profilesError);
  } else if (profilesData) {
    profilesData.forEach((profile: any) => {
      results.push({
        type: 'profile',
        id: profile.id,
        username: profile.username,
        avatar_url: profile.avatar_url,
        verification_status: profile.verification_status,
      });
    });
  }

  return results;
};

// Paginated product search with optional filters
export const searchProductsPaginated = async (
  searchTerm: string | null,
  offset = 0,
  limit = 20,
  minPrice?: number | null,
  maxPrice?: number | null,
  sellerUsername?: string | null
): Promise<ProductItem[]> => {
  const lower = searchTerm ? `%${searchTerm.toLowerCase().replace(/\s+/g, '%')}%` : null;
  let query: any = supabase
    .from('products')
    .select('id,store_id,title,description,cover_url,main_image_url,image_urls,created_at,seller_id,profiles!books_owner_id_fkey(username,avatar_url,verification_status),stores!products_store_id_fkey(id,name,slug,avatar_url,owner_id,latitude,longitude),fixed_sale_price,stock,is_auction,condition')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (lower) {
    query = query.or(`title.ilike.${lower},description.ilike.${lower},author.ilike.${lower}`);
  }

  if (typeof minPrice === 'number') {
    query = query.gte('fixed_sale_price', minPrice);
  }
  if (typeof maxPrice === 'number') {
    query = query.lte('fixed_sale_price', maxPrice);
  }

  if (sellerUsername) {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', sellerUsername)
      .single();
    if (!profileError && profile) {
      query = query.eq('seller_id', profile.id);
    }
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error searching products paginated:', error);
    return [];
  }
  return (data || []).map((p: any) => ({
    id: p.id,
    store_id: p.store_id ?? null,
    title: p.title,
    description: p.description,
    cover_url: p.cover_url,
    main_image_url: p.main_image_url,
    image_urls: p.image_urls || [],
    created_at: p.created_at,
    seller_id: p.seller_id,
    fixed_sale_price: p.fixed_sale_price ?? null,
    stock: p.stock ?? null,
    is_auction: p.is_auction ?? false,
    condition: p.condition ?? null,
    username: p.profiles ? p.profiles.username : null,
    verification_status: p.profiles ? p.profiles.verification_status : null,
    stores: p.stores || null,
  }));
};

// Paginated notes search with optional category filter
export const searchNotesPaginated = async (
  searchTerm: string | null,
  offset = 0,
  limit = 20,
  categoryId?: string | null
): Promise<NoteItem[]> => {
  const lower = searchTerm ? `%${searchTerm.toLowerCase()}%` : null;
  let query: any = supabase
    .from('notes')
    .select('id,title,content,created_at,user_id,photo_url,image_urls,profiles(username,avatar_url,verification_status),category_id')
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (lower) {
    query = query.or(`title.ilike.${lower},content.ilike.${lower}`);
  }

  if (categoryId) {
    query = query.eq('category_id', categoryId);
  }

  const { data, error } = await query;
  if (error) {
    console.error('Error searching notes paginated:', error);
    return [];
  }
  return (data || []).map((n: any) => ({
    id: n.id,
    title: n.title,
    content: n.content,
    created_at: n.created_at,
    user_id: n.user_id,
    photo_url: n.photo_url || null,
    image_urls: n.image_urls || null,
    username: n.profiles ? n.profiles.username : null,
    verification_status: n.profiles ? n.profiles.verification_status : null,
    category_id: n.category_id || null,
  }));
};

// Simple autocomplete suggestions (products titles, note titles, usernames)
export const getSearchSuggestions = async (searchTerm: string, limit = 5) => {
  if (!searchTerm.trim()) return [];
  const q = `%${searchTerm.toLowerCase()}%`;
  const suggestions: Array<{type: string; id: string; label: string}> = [];

  // products
  const { data: prod } = await supabase.from('products').select('id,title').or(`title.ilike.${q}`).limit(limit);
  if (prod) prod.forEach((p: any) => suggestions.push({ type: 'product', id: p.id, label: p.title }));

  // notes
  const { data: notes } = await supabase.from('notes').select('id,title').or(`title.ilike.${q}`).limit(limit);
  if (notes) notes.forEach((n: any) => suggestions.push({ type: 'note', id: n.id, label: n.title || 'Nota' }));

  // users
  const { data: users } = await supabase.from('profiles').select('id,username').ilike('username', q).limit(limit);
  if (users) users.forEach((u: any) => suggestions.push({ type: 'profile', id: u.username, label: `@${u.username}` }));

  return suggestions.slice(0, limit);
};